import { Fragment, memo, useCallback, useEffect, useRef } from "react";
import { useRecoilValue, useResetRecoilState } from "recoil";
import { Button, Grid } from "@mui/material";
import { useInfiniteListFutureOffers, useInfiniteListOffers } from "../../../api/hooks";
import {
  filterSelectedAttributeIdsSelector,
  sortByPriceOrIsNewAtom,
  minPriceAtom,
  maxPriceAtom,
  searchFormSelector,
  filterSelectedAttributeIdsAtom,
  formattedBoundCoordsSelector,
} from "../../../state/search-state";
import { adultsCounterAtom, childrenCounterAtom } from "../../../state/booking-state";
import { useTranslation } from "../../../fe-ui/hooks/use-translation";
import { useEmbeddedContext } from "../../EmbeddedContext";
import { LoadingState } from "./compounds/LoadingState";
import { EmptyContent } from "../../../fe-ui/blocks/EmptyContent/EmptyContent";
import { PropertyCardComponent } from "./compounds/PropertyCardComponent";
import { LoadNextButton } from "./compounds/LoadNextButton";
import { OffersListProps } from "./Props";
import { BannersSearch } from "../../components/property/BannersSearch";
import { SearchOffersResponse } from "../../../api/fetchOffers";
import { FutureOffersResponse } from "../../../api/fetchFutureOffers";

const bannerSizeBy: Record<"one" | "two" | "four", number> = {
  one: 1,
  two: 2,
  four: 4,
};

export const OffersList = memo(function OffersList(props: OffersListProps) {
  const {
    bannersSearch,
    bannerBlockId,
    perPage = 4,
    withAttributesContext,
    withGuestsContext,
    withBoundCoordsContext,
    infinite = true,
    cityIdContext: cityId,
    hotelIdContext: hotelId,
    type,
    initialPageIdx,
    guestsCount = 2,
    showTotalPrice = false,
    dataTestId,
    disableOpenInNewTab,
    forcedAttributeIds,
    forcedDepartureDate, // New prop
    forcedArrivalDate, // New prop
    onFetchSuccess,
    compactWidth,
  } = props;

  const selectedAttributeIds = useRecoilValue(filterSelectedAttributeIdsSelector);
  const sortOrder = useRecoilValue(sortByPriceOrIsNewAtom);
  const minPrice = useRecoilValue(minPriceAtom);
  const maxPrice = useRecoilValue(maxPriceAtom);
  const totalAdults = useRecoilValue(adultsCounterAtom);
  const totalChildren = useRecoilValue(childrenCounterAtom);
  const filtersValue = useRecoilValue(searchFormSelector);
  const { dateRange } = filtersValue;
  const [arrivalDate, departureDate] = dateRange;
  const boundCoords = useRecoilValue(formattedBoundCoordsSelector);
  const t = useTranslation();
  const resetAttributes = useResetRecoilState(filterSelectedAttributeIdsAtom);
  const { basePath } = useEmbeddedContext();

  const currentArrivalDate = forcedArrivalDate || (arrivalDate && arrivalDate.format("YYYY-MM-DD")) || "";
  const currentDepartureDate = forcedDepartureDate || (departureDate && departureDate.format("YYYY-MM-DD")) || "";

  const pageSearchBanners = bannersSearch?.filter((banner) => banner.attributes?.blockId === bannerBlockId);
  const firstPageBanner = pageSearchBanners?.filter((banner) => banner.attributes?.page === 1);
  const currentPerPage = useRef(
    firstPageBanner
      ? perPage - firstPageBanner.reduce((acc, banner) => acc + bannerSizeBy[banner.attributes?.size || "one"], 0)
      : perPage
  );

  const isFuture = type === "future";
  const useList = isFuture ? useInfiniteListFutureOffers : useInfiniteListOffers;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status } = useList(
    {
      bannerBlockId,
      perPage: currentPerPage.current,
      arrivalDate: isFuture ? "" : currentArrivalDate,
      departureDate: isFuture ? "" : currentDepartureDate,
      cities: cityId ? cityId.toString() : undefined,
      hotelIds: hotelId ? hotelId : undefined,
      attributeIds: forcedAttributeIds
        ? forcedAttributeIds.join(",")
        : withAttributesContext
        ? [
            ...selectedAttributeIds,
            ...(sortOrder === "is_new"
              ? [
                  // TODO: Move to config
                  952,
                ]
              : []),
          ].join(",")
        : undefined,
      adults: withGuestsContext ? totalAdults : guestsCount,
      children: withGuestsContext ? totalChildren : 0,
      sortOrder: sortOrder === "is_new" ? "asc" : sortOrder,
      minPrice,
      maxPrice,
      locationCoordinates: withBoundCoordsContext ? boundCoords : undefined,
    },
    { onSuccess: onFetchSuccess }
  );

  useEffect(() => {
    if (firstPageBanner && firstPageBanner.length > 0) {
      currentPerPage.current =
        perPage - firstPageBanner.reduce((acc, banner) => acc + bannerSizeBy[banner.attributes?.size || "one"], 0);
    } else {
      currentPerPage.current = perPage;
    }
  }, [firstPageBanner, perPage]);

  const handleLoadNext = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Проверяем текущую страницу
      const currentPage = (data?.pages.length ?? 0) + 1;
      const foundBanner = pageSearchBanners?.filter((banner) => banner.attributes?.page === currentPage);

      // Обновляем текущий размер страницы перед загрузкой
      if (foundBanner && foundBanner.length > 0) {
        currentPerPage.current =
          perPage - foundBanner.reduce((acc, banner) => acc + bannerSizeBy[banner.attributes?.size || "one"], 0);
      } else {
        currentPerPage.current = perPage;
      }

      // Загружаем следующую страницу
      void fetchNextPage();
    },
    [data, fetchNextPage, pageSearchBanners, perPage]
  );

  useEffect(() => {
    if (data && data.pages.length === 1 && initialPageIdx === 1) {
      void fetchNextPage();
    }
  }, [initialPageIdx, fetchNextPage, data]);

  const loadNextIsDisabled = !hasNextPage || isFetchingNextPage;

  if (status === "loading") return <LoadingState counter={perPage} compactWidth={compactWidth} />;

  if (data === undefined || data?.pages?.[0].offers.length === 0 || status === "error") {
    return (
      <EmptyContent
        filled
        title={t("search-nothing-found-title")}
        description={
          <>
            {t.rich("search-nothing-found-try-different-search", {
              br: () => <br />,
              clearfilter: (x) => (
                <Button onClick={resetAttributes} size="small" variant="text">
                  {x}
                </Button>
              ),
              changedates: () => <a />,
            })}
          </>
        }
      />
    );
  }

  return (
    <>
      <Grid container spacing={4} data-testid={dataTestId}>
        {data.pages.map((page: FutureOffersResponse | SearchOffersResponse, pageIdx) =>
          !initialPageIdx || pageIdx >= initialPageIdx ? (
            <Fragment key={pageIdx}>
              {page.offers.map((offer, offerIdx) => {
                const bannerCurrentPage = initialPageIdx === 1 ? pageIdx : pageIdx + 1;

                const foundBanner = pageSearchBanners?.find(
                  (banner) => banner.attributes?.page === bannerCurrentPage && banner.attributes?.position === offerIdx
                );

                return (
                  <Fragment key={offer.unitGroupId}>
                    {foundBanner && pageSearchBanners && pageSearchBanners.length > 0 && (
                      <BannersSearch banners={pageSearchBanners} currentPage={bannerCurrentPage} position={offerIdx} />
                    )}

                    <PropertyCardComponent
                      key={offer.unitGroupId}
                      offer={offer}
                      basePath={basePath}
                      arrivalDate={currentArrivalDate}
                      departureDate={currentDepartureDate}
                      totalAdults={totalAdults}
                      totalChildren={totalChildren}
                      showTotalPrice={showTotalPrice}
                      disableOpenInNewTab={disableOpenInNewTab}
                      compactWidth={compactWidth}
                    />
                  </Fragment>
                );
              })}
            </Fragment>
          ) : null
        )}
      </Grid>
      {infinite && (
        <LoadNextButton
          onClick={handleLoadNext}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          t={t}
          loadNextIsDisabled={loadNextIsDisabled}
        />
      )}
    </>
  );
});
